// Topbar — minimal header sitting on top of the main column.
//
// Search input is a stub (Phase 2 wires it to the actual command palette).

import { LogOut, Menu, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUser } from '@/hooks/useUser';
import { initials } from '@/lib/format';
import { signOut } from '@/lib/auth';

export function Topbar({
  onToggleSidebar,
}: {
  onToggleSidebar?: () => void;
}): JSX.Element {
  const user = useUser();

  async function handleSignOut(): Promise<void> {
    await signOut();
    // Hard-redirect so the router resets and AuthGate flips.
    window.location.href = '/login';
  }

  return (
    <header
      className="flex items-center gap-4 border-b border-[var(--color-border)] bg-[var(--color-surface)]"
      style={{ height: 'var(--topbar-h, 64px)', paddingInline: 24 }}
    >
      {onToggleSidebar ? (
        // `md:!hidden` (with the important modifier) is required because
        // `.btn` declares `display: inline-flex` from components.css,
        // which is imported AFTER Tailwind utilities and would otherwise
        // win the cascade. Without `!hidden` the hamburger renders on
        // desktop but does nothing (the drawer is only mounted as a
        // mobile dialog), confusing the user.
        <button
          type="button"
          onClick={onToggleSidebar}
          className="btn btn-ghost btn-sm btn-icon md:!hidden"
          aria-label="Toggle navigation"
        >
          <Menu size={18} />
        </button>
      ) : null}

      <div className="relative flex-1 max-w-[480px]">
        <Search
          size={14}
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-fg-subtle)]"
        />
        <Input
          type="search"
          placeholder="Global search — coming in Phase 3"
          className="field-input-sm"
          // `field-input-sm` hard-codes `padding: 0 10px`, which would
          // sit the placeholder behind the absolute-positioned icon.
          // An inline style wins the specificity battle without
          // touching the shared field token.
          style={{ paddingLeft: 36 }}
          aria-label="Search (not yet wired)"
          disabled
          title="Global search ships in Phase 3. For now, use the search box on each list page."
        />
      </div>

      <div className="ml-auto flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-[var(--color-surface-sunken)]"
              aria-label="Account menu"
            >
              <span
                className="inline-flex items-center justify-center rounded-full font-display font-semibold"
                style={{
                  width: 32,
                  height: 32,
                  background: 'var(--color-forest-700)',
                  color: 'var(--color-cream-50)',
                  fontSize: 12,
                  letterSpacing: '0.04em',
                }}
              >
                {initials(user?.displayName ?? user?.email ?? null)}
              </span>
              <span
                className="hidden sm:inline text-left"
                style={{ fontSize: 13, lineHeight: '16px' }}
              >
                <span className="block font-medium text-[var(--color-fg)]">
                  {user?.displayName ?? 'Admin'}
                </span>
                <span className="block text-[var(--color-fg-muted)]">
                  {user?.email ?? '—'}
                </span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={6}>
            <DropdownMenuLabel>{user?.email ?? 'Signed out'}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleSignOut} danger>
              <LogOut size={14} aria-hidden="true" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
